import React, { useState, useEffect, createContext, useContext } from 'react';
import { ThemeProvider, CssBaseline, Box, Typography, Button, CircularProgress } from '@mui/material';
import theme from './theme';
import { C } from './theme';
import Login from './pages/Login';
import Onboarding from './pages/Onboarding';
import Browse from './pages/Browse';
import Chats from './pages/Chats';
import ChatConversation from './pages/ChatConversation';
import IdentifierSetup from './pages/IdentifierSetup';
import CheckInForm from './pages/CheckInForm';
import VenueHome from './pages/VenueHome';
import GamePage from './pages/GamePage';
import AppShell from './AppShell';
import Home from './pages/Home';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Legal from './pages/Legal';
import Support from './pages/Support';
import Waitlist from './pages/Waitlist';
import api from './services/api';
import SplashScreen from './pages/SplashScreen';
import VenueLogin from './pages/VenueLogin';
import VenueDashboard from './pages/VenueDashboard';
import VenueSpectator from './pages/VenueSpectator';
import VenueAuthGuard from './pages/VenueAuthGuard';
import VenueSettings from './pages/VenueSettings';
import SuperAdmin from './pages/SuperAdmin';
import VenueVerify from './pages/VenueVerify';
import VenueOnboardingAdmin from './pages/VenueOnboardingAdmin';
import { MatchOverlay } from './components/ScoreAnimations';
import ErrorBoundary from './components/ErrorBoundary';

export const NavContext  = createContext(null);
export const UserContext = createContext(null);

export function useNav()  { return useContext(NavContext);  }
export function useUser() { return useContext(UserContext); }

// ─── Blocked screen (inline — no route) ──────────────────────────────────────
function BlockedScreen() {
  function handleLogout() {
    localStorage.removeItem('binko_token');
    window.location.href = '/';
  }
  return (
    <Box sx={{
      minHeight: '100vh',
      background: C.bgApp,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '32px 24px',
      textAlign: 'center',
      gap: '16px',
    }}>
      <img src="/logo.png" alt="binko" style={{ height: '44px', width: 'auto', marginBottom: '8px' }} />
      <Typography sx={{ fontSize: '24px' }}>🚫</Typography>
      <Typography sx={{ fontSize: '18px', fontWeight: 600, color: C.textPrimary }}>
        Account unavailable
      </Typography>
      <Typography sx={{ fontSize: '14px', color: C.textMuted, lineHeight: 1.6, maxWidth: '300px' }}>
        Your account is not able to access Binko right now. If you think this is a mistake, please contact support.
      </Typography>
      <Button
        onClick={handleLogout}
        sx={{
          mt: '8px',
          border: `0.5px solid rgba(255,255,255,0.15)`,
          color: C.textMuted,
          borderRadius: '10px',
          padding: '8px 20px',
          fontSize: '13px',
          '&:hover': { background: 'rgba(255,255,255,0.05)' },
        }}
      >
        Log out
      </Button>
    </Box>
  );
}

function App() {
  const [path, setPath]   = useState(window.location.pathname);
  const [user, setUser]   = useState(null);   // null = not yet loaded
  const [userLoading, setUserLoading] = useState(!!localStorage.getItem('binko_token'));
  const [showWarning, setShowWarning] = useState(false);
  const [showSplash, setShowSplash] = useState(!sessionStorage.getItem('binko_splash_seen'));
  const [matchOverlay, setMatchOverlay] = useState({ visible: false, matchId: '', theirAlias: '' });

  // ── Match overlay triggers ────────────────────────────────────────────────
  useEffect(() => {
    // 1. Cold open: notification tap passed matchId in URL
    const params = new URLSearchParams(window.location.search);
    const pendingMatchId    = params.get('match_overlay') || '';
    const pendingTheirAlias = params.get('their_alias')   || '';
    if (pendingMatchId && !sessionStorage.getItem('match_overlay_seen_' + pendingMatchId)) {
      setMatchOverlay({ visible: true, matchId: pendingMatchId, theirAlias: pendingTheirAlias });
      sessionStorage.setItem('match_overlay_seen_' + pendingMatchId, '1');
      window.history.replaceState({}, '', window.location.pathname);
    }

    // 2. Foreground FCM: binko:match CustomEvent from firebase.js
    const onFcmMatch = (e) => {
      const { matchId, theirAlias } = e.detail || {};
      if (matchId && !sessionStorage.getItem('match_overlay_seen_' + matchId)) {
        setMatchOverlay({ visible: true, matchId, theirAlias: theirAlias || '' });
        sessionStorage.setItem('match_overlay_seen_' + matchId, '1');
      }
    };
    window.addEventListener('binko:match', onFcmMatch);

    // 3. SW postMessage: tab was open but backgrounded when notification arrived
    const onSwMessage = (e) => {
      if (e.data?.type === 'binko:match') {
        const { matchId, theirAlias } = e.data;
        if (matchId && !sessionStorage.getItem('match_overlay_seen_' + matchId)) {
          setMatchOverlay({ visible: true, matchId, theirAlias: theirAlias || '' });
          sessionStorage.setItem('match_overlay_seen_' + matchId, '1');
        }
      }
    };
    navigator.serviceWorker?.addEventListener('message', onSwMessage);

    return () => {
      window.removeEventListener('binko:match', onFcmMatch);
      navigator.serviceWorker?.removeEventListener('message', onSwMessage);
    };
  }, []);

  // ── Load user from /auth/me on app start (if token present) ─────────────────
  useEffect(() => {
    const token = localStorage.getItem('binko_token');
    if (!token) {
      setUserLoading(false);
      return;
    }
    async function loadUser(isRetry) {
      try {
        const r = await api.get('/auth/me');
        if (r.data.status === 'success') {
          setUser(r.data.user); // includes effectiveStatus
          if (r.data.show_warning) setShowWarning(true);
        } else {
          // Token invalid/expired — clear and show login
          localStorage.removeItem('binko_token');
        }
        setUserLoading(false);
      } catch (err) {
        // Only clear the token on a confirmed auth failure (401/403).
        // Network errors, timeouts, and 5xx must NOT log the user out —
        // the token may still be valid; the server was just unreachable.
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          localStorage.removeItem('binko_token');
          setUserLoading(false);
          return;
        }
        if (!isRetry) {
          // Transient failure (network/timeout/5xx) — retry once after a
          // short delay before giving up. Token is left intact either way.
          setTimeout(() => loadUser(true), 3000);
        } else {
          setUserLoading(false);
        }
      }
    }
    loadUser(false);
  }, []);

  // ── Re-route when user effectiveStatus changes (e.g. after redeem) ──────────
  useEffect(() => {
    if (!user) return;
    if (user.effectiveStatus === 'WAITLISTED' && path !== '/waitlist' && path !== '/profile' && path !== '/settings') {
      navigate('/waitlist');
    } else if (user.effectiveStatus === 'ACTIVE' && path === '/waitlist') {
      navigate('/home');
    }
    // BLOCKED is handled inline — no route change needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.effectiveStatus]);

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);


  function navigate(to) {
    window.history.pushState({}, '', to);
    setPath(to);
  }

  function renderPage() {
    // Super admin route
    if (path.startsWith('/superadmin'))  return <SuperAdmin />;
    if (path === '/verify-venue')       return <VenueVerify />;
    if (path === '/venue-onboarding')   return <VenueOnboardingAdmin />;
    // Venue admin routes — intercepted before user auth logic
    if (path === '/venue/login')     return <VenueLogin />;
    if (path === '/venue/spectator') return <VenueAuthGuard><VenueSpectator /></VenueAuthGuard>;
    if (path === '/venue/settings')   return <VenueAuthGuard><VenueSettings /></VenueAuthGuard>;
    if (path.startsWith('/venue/'))  return <VenueAuthGuard><VenueDashboard /></VenueAuthGuard>;

    const token = localStorage.getItem('binko_token');

    // ── Still fetching /auth/me ──────────────────────────────────────────────
    if (userLoading) {
      return (
        <Box sx={{
          minHeight: '100vh',
          background: C.bgApp,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <CircularProgress size={28} sx={{ color: C.teal }} />
        </Box>
      );
    }

    // ── No token → Login ─────────────────────────────────────────────────────
    if (!token) return <Login />;

    // ── Outside shell — no bottom nav ────────────────────────────────────────
    if (path === '/onboarding')        return <Onboarding />;
    if (path === '/identifier-setup')  return <IdentifierSetup />;
    if (path === '/checkin-form')      return <CheckInForm />;
    if (path === '/waitlist')          return <Waitlist />;
    if (path.startsWith('/chat/'))     return <ChatConversation />;

    // ── Blocked (inline — never sees the app) ────────────────────────────────
    if (user?.effectiveStatus === 'BLOCKED') return <BlockedScreen />;

    // ── Waitlisted users forced to /waitlist ─────────────────────────────────
    if (user?.effectiveStatus === 'WAITLISTED' && path !== '/profile' && path !== '/settings') return <Waitlist />;

    // ── Inside shell — bottom nav visible ───────────────────────────────────
    let pageContent;
    if (path === '/home')                 pageContent = <Home />;
    else if (path === '/inhere')          pageContent = <Browse />;
    else if (path === '/browse')          pageContent = <Browse />;
    else if (path === '/game')             pageContent = <GamePage />;
    else if (path === '/chats')           pageContent = <Chats />;
    else if (path === '/profile')         pageContent = <Profile />;
    else if (path === '/settings')        pageContent = <Settings />;
    else if (path === '/legal/terms')     pageContent = <Legal page="terms" />;
    else if (path === '/legal/privacy')   pageContent = <Legal page="privacy" />;
    else if (path === '/legal/game-rules') pageContent = <Legal page="game-rules" />;
    else if (path === '/support')         pageContent = <Support page="contact" />;
    else if (path === '/support/report')  pageContent = <Support page="report" />;
    else if (path === '/venue-home')      pageContent = <VenueHome />;
    else                                  pageContent = <Home />;

    return <AppShell path={path}>{pageContent}</AppShell>;
  }

  return (
    <ErrorBoundary>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <NavContext.Provider value={navigate}>
        {showWarning && (
          <Box onClick={() => setShowWarning(false)} sx={{
            position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)',
            zIndex: 9999, width: '90%', maxWidth: 420,
            background: '#854F0B', borderRadius: 2, px: 2.5, py: 1.5,
            display: 'flex', alignItems: 'flex-start', gap: 1.5, cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
          }}>
            <Typography sx={{ fontSize: '1.1rem', lineHeight: 1 }}>&#9888;</Typography>
            <Box>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#FAEEDA', mb: 0.3 }}>
                Safety notice
              </Typography>
              <Typography sx={{ fontSize: '0.73rem', color: '#FAC775', lineHeight: 1.4 }}>
                Your account has received a safety notice. Please review our community guidelines. Continued reports may affect your access.
              </Typography>
            </Box>
          </Box>
        )}
        <UserContext.Provider value={{ user, setUser }}>
          <MatchOverlay
            visible={matchOverlay.visible}
            myAlias={user?.alias || ''}
            theirAlias={matchOverlay.theirAlias}
            onDismiss={() => setMatchOverlay(s => ({ ...s, visible: false }))}
            onChat={() => { setMatchOverlay(s => ({ ...s, visible: false })); navigate('/chat/' + matchOverlay.matchId); }}
          />
          {showSplash ? <SplashScreen onDone={() => { sessionStorage.setItem('binko_splash_seen', '1'); setShowSplash(false); }} /> : renderPage()}
        </UserContext.Provider>
      </NavContext.Provider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
